import { bool, func, object, string } from "prop-types";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";

import styled from "styled-components";

import { toolbarButtonSize, toolbarPadding } from "NavBar/styleConstants";
import { motion } from "motion/react";

import FnrButtonWrapper from "UI/FnrButtonWrapper";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrInput3 from "UI/FnrInput3";
import DefaultIcon from "UI/FnrInput3/Decorators/DefaultIcon";
import DefaultInput from "UI/FnrInput3/Inputs/DefaultInput";
import FnrOpacityToggle from "UI/FnrOpacityToggle";
import FnrTappyShrink from "UI/FnrTappyShrink";
import { colorsByFunction, marketingColors } from "UI/colors";

import useIsHoverFocused from "Utilities/Hooks/useIsHoverFocused";

const AlmostDefaultInput = forwardRef((rest, ref) => (
  <SDefaultInput
    name="super_search_input"
    className="super_search_input loaded"
    ref={ref}
    {...rest}
  />
));

const SDefaultInput = styled(DefaultInput)`
  border-radius: ${toolbarPadding + toolbarButtonSize}px;
  height: ${toolbarButtonSize}px;
  width: 100%;
  visibility: visible;
  caret-color: ${({ $caretActive }) =>
    $caretActive ? marketingColors.fnBlue : "transparent"};
  transition: box-shadow 0.6s, caret-color 0.3s;
  &:focus,
  &:hover {
    transition: box-shadow 0.2s, caret-color 0.3s;
  }
`;

const InputIcon = styled(DefaultIcon)`
  color: ${colorsByFunction.textSecondaryLight};
`;

const Input = ({ isActive, setIsActive, toggleRef, value, setValue }) => {
  const [showClose, setShowClose] = useState(false);
  const inputRef = useRef();
  const focusTimeoutRef = useRef();

  const hideSearchResults = useCallback(() => {
    if (typeof window.superSearchObj?.hideSearchResults === "function")
      window.superSearchObj?.hideSearchResults();
    setIsActive(false);
    setShowClose(false); // should be hidden by the input being inactive anyway.
    setValue("");
    clearTimeout(focusTimeoutRef.current);
    focusTimeoutRef.current = setTimeout(() => toggleRef.current.focus());
  }, [setIsActive, setValue, toggleRef]);

  const showSearchResults = useCallback(() => {
    if (
      typeof window.superSearchObj?.showSearchResults === "function" &&
      value.length > 2
    )
      window.superSearchObj?.showSearchResults();
    setShowClose(value.length > 2);
    setIsActive(true);
  }, [setIsActive, value.length]);

  const handleChange = useCallback(({ target: { value: newValue } }) => {
    setShowClose(newValue.length > 2);
  }, []);

  const handleBlur = useCallback(() => {
    if (value.length <= 2) hideSearchResults();
  }, [value, hideSearchResults]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") hideSearchResults();
    },
    [hideSearchResults]
  );

  useEffect(() => {
    if (isActive) inputRef.current.focus();
  }, [isActive]);

  useEffect(() => {
    return () => clearTimeout(focusTimeoutRef.current);
  }, []);

  const xRef = useRef();
  const xIsHoverFocused = useIsHoverFocused(xRef);

  return (
    <Wrapper>
      <FnrInput3
        $caretActive={isActive}
        placeholder="Super Search"
        data-canviewusers="1"
        Component={AlmostDefaultInput}
        data-cy="NavBar_SuperSearch"
        ref={inputRef}
        onFocus={showSearchResults}
        onChange={handleChange}
        value={value}
        setValue={setValue}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        PrefixComponent={InputIcon}
        icon="search far"
        autoComplete="off"
        tabIndex={isActive ? 0 : -1}
      />

      <SFnrOpacityToggle show>
        <FnrTappyShrink>
          <FnrButtonWrapper
            onClick={hideSearchResults}
            data-cy="NavBar_SuperSearch_ClearButton"
            ref={xRef}
          >
            <SFnrContainedIcon
              backgroundColor={
                xIsHoverFocused ? "textBlack" : "textSecondaryLight"
              }
              color="textLight"
              icon="fa-times fas"
            />
          </FnrButtonWrapper>
        </FnrTappyShrink>
      </SFnrOpacityToggle>
    </Wrapper>
  );
};

Input.propTypes = {
  isActive: bool.isRequired,
  setIsActive: func.isRequired,
  toggleRef: object,
  value: string.isRequired,
  setValue: func.isRequired,
};

const SFnrContainedIcon = styled(FnrContainedIcon)`
  border-radius: 50%;
`;

const SFnrOpacityToggle = styled(FnrOpacityToggle)`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
`;

const Wrapper = styled(motion.div)`
  position: relative;
  width: 100%;
`;

export default Input;
